import { debounce } from 'lodash';

export class PartnersListingsFilter {
  constructor(component) {
    this.refs = {
      filterGroup: '.js-filter-group',
      groupToggle: '.js-group-toggle',
      groupToggleIcon: '.js-group-toggle-icon',
      optionsList: '.js-options-list',
      option: '.js-option',
      mainToggle: '.js-main-toggle',
      mainToggleIcon: '.js-main-toggle-icon',
      groupsWrapper: '.js-groups-wrapper',
      formInner: '.js-form-inner',
      filterReset: '.js-reset',
      liveRegion: '.js-filters-live',
      mainToggleText: '.js-main-toggle-text',
    };

    this.dom = {
      $component: component,
      $groups: component.querySelectorAll(this.refs.filterGroup),
      $groupToggles: component.querySelectorAll(this.refs.groupToggle),
      $options: component.querySelectorAll(this.refs.option),
      $mainToggle: component.querySelector(this.refs.mainToggle),
      $mainToggleIcon: component.querySelector(this.refs.mainToggleIcon),
      $groupsWrapper: component.querySelector(this.refs.groupsWrapper),
      $formInner: component.querySelector(this.refs.formInner),
      $filterReset: component.querySelector(this.refs.filterReset),
      $liveRegion: component.querySelector(this.refs.liveRegion),
      $mainToggleText: component.querySelector(this.refs.mainToggleText),
    };

    this.state = {
      optionsListHeights: new Map(),
      formInnerHeight: 0,
      innerWidth: window.innerWidth,
      checkedCount: 0,
      location: component.dataset?.location,
      activeFilters: [],
      mobileClearButtonOffset: 54,
    }

    this.updateInputs = this.updateInputs.bind(this);
    this.updateState = this.updateState.bind(this);
    this.handleMainToggle = this.handleMainToggle.bind(this);
    this.onGroupToggleClick = this.onGroupToggleClick.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleFilterReset = this.handleFilterReset.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleResize = debounce(this.handleResize.bind(this), 200);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.updateCheckedCount = this.updateCheckedCount.bind(this);

    this.mount();
    this.init();
  }

  mount() {
    const { dom } = this;

    dom.$mainToggle?.addEventListener('click', this.handleMainToggle);
    dom.$groupToggles?.forEach($btn => $btn.addEventListener('click', this.onGroupToggleClick));
    dom.$component.addEventListener('change', this.handleFilterChange);
    dom.$filterReset.addEventListener('click', this.handleFilterReset);
    dom.$component.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('click', this.handleDocumentClick);
  }

  init() {
    this.setInitialOptionsListHeights();
    this.setInitialFormInnerHeight();
  }

  updateState(state) {
    this.updateInputs(state.activeFilters);
    this.updateCheckedCount(state.count);
  }

  setInitialOptionsListHeights() {
    const { dom, refs, state } = this;

    dom.$groups.forEach($group => {
      const $optionsList = $group.querySelector(refs.optionsList);
      if (!$optionsList) return;

      // If desktop add 25px for top padding on each group
      const naturalHeight = $optionsList.scrollHeight + (state.location === 'desktop' ? 25 : 0);
      state.optionsListHeights.set($group, naturalHeight);
    });
  }

  setInitialFormInnerHeight() {
    const { dom, state } = this;
    if (!dom.$formInner) return;

    state.formInnerHeight = dom.$groupsWrapper.scrollHeight + (state.location === 'desktop' ? 54 : 25);
  }

  handleMainToggle() {
    const { dom, state } = this;

    const isCollapsed =
      dom.$component.classList.toggle('is-collapsed');

    dom.$mainToggle.setAttribute('aria-expanded', String(!isCollapsed));
    dom.$formInner.inert = isCollapsed;
    dom.$mainToggleIcon.textContent = isCollapsed ? '+' : '-';
    dom.$formInner.style.maxHeight = (isCollapsed ? '0' : state.formInnerHeight) + 'px';
  }

  onGroupToggleClick(e) {
    const { dom, refs } = this;
    const $button = e.currentTarget;
    const $group = $button.closest(refs.filterGroup);
    const $options = $group.querySelector(refs.optionsList);

    if (!$options) return;

    dom.$groupToggles.forEach($toggle => {
      if ($toggle === $button) return;
      const $toggleGroup = $toggle.closest(refs.filterGroup);
      if ($toggleGroup.classList.contains('collapsed')) return;
      this.toggleGroup($toggle);
    });

    this.toggleGroup($button);
  }

  toggleGroup($button) {
    const { dom, refs, state } = this;
    const $group = $button.closest(refs.filterGroup);
    const $icon = $button.querySelector(refs.groupToggleIcon);
    const $options = $group.querySelector(refs.optionsList);
    const optionsListHeight = state.optionsListHeights.get($group);

    const isCollapsed = $group.classList.toggle('collapsed');
    $options.inert = isCollapsed ? true : false;
    $options.style.maxHeight = isCollapsed ?
      '0' :
      optionsListHeight + 'px';
    $icon.textContent = isCollapsed ? '+' : '−';
    $button.setAttribute('aria-expanded', String(!isCollapsed));

    const currentFormInnerMaxHeight = state.formInnerHeight;

    // On mobile, need to offset by 17px since the options list is adjacent to the group name rather than below it.
    const offset = state.location === 'mobile' ?
      (optionsListHeight < 17 ? optionsListHeight : 17) :
       0;
    const newMaxHeight = isCollapsed ?
      currentFormInnerMaxHeight - optionsListHeight + offset :
      currentFormInnerMaxHeight + optionsListHeight - offset;

    dom.$formInner.style.maxHeight = newMaxHeight + 'px';
    state.formInnerHeight = newMaxHeight;
  }

  handleFilterChange(e) {
    const { dom, refs } = this;

    const input = e.target.closest(refs.option);
    if (!input) return;

    dom.$component.dispatchEvent(
      new CustomEvent('filters:change', {
        bubbles: true,
        detail: {
          value: input.value,
          checked: input.checked,
        },
      })
    );
  }

  handleFilterReset() {
    const { dom } = this;

    dom.$component.dispatchEvent(
      new CustomEvent('filters:reset', {
        bubbles: true,
      })
    );
  }

  handleKeydown(e) {
    const { refs } = this;
    const checkbox = e.target.closest(refs.option);
    if (!checkbox || e.key !== 'Enter') return;

    e.preventDefault();
    checkbox.click();
  }

  handleResize() {
    const { dom, refs, state } = this;

    if (state.innerWidth === window.innerWidth) return;
    state.innerWidth = window.innerWidth;

    // Clear cached heights
    state.optionsListHeights.clear();

    // Reset each group
    dom.$groupToggles.forEach($toggle => {
      const $toggleGroup = $toggle.closest(refs.filterGroup);
      if ($toggleGroup.classList.contains('collapsed')) return;
      this.toggleGroup($toggle);
    });

    if (!dom.$component.classList.contains('is-collapsed')) this.handleMainToggle();

    // Recalculate heights
    setTimeout(() => {
      this.setInitialOptionsListHeights();
      this.setInitialFormInnerHeight();

      if (state.location !== 'mobile' || state.checkedCount === 0) return;
      state.formInnerHeight = state.formInnerHeight + state.mobileClearButtonOffset;
    }, 300);
  }

  handleDocumentClick(e) {
    const target = e.target;

    const { dom, state } = this;
    if (state.location !== 'mobile') return;
    if (dom.$mainToggle.contains(target)) return;
    if (dom.$component.classList.contains('is-collapsed')) return;

    if (
      dom.$formInner.contains(target) ||
      document.querySelector('.js-section-header').contains(target)
    ) {
      return;
    }

    this.handleMainToggle();
  }

  updateInputs(activeFilters) {
    const { dom, state } = this;

    state.activeFilters = activeFilters;

    const inputs = dom.$options;

    // Sync checkbox states across modules
    inputs.forEach(input => {
      const shouldBeChecked = state.activeFilters.includes(input.value);
      if (input.checked !== shouldBeChecked) {
        input.checked = shouldBeChecked;
      }
    });
  }

  updateCheckedCount(count) {
    const { state } = this;

    const originalCheckedCount = state.checkedCount;
    state.checkedCount = count;

    this.updateActiveFilterClass();
    this.updateMainToggleText();
    this.updateLiveRegion();

    // If changed from 0 to nonzero or nonzero to 0
    if (
      (originalCheckedCount === 0 && state.checkedCount > 0) ||
      (originalCheckedCount > 0 && state.checkedCount === 0)
    ) {
      this.updateMaxHeightIfRequired();
    }
  }

  updateActiveFilterClass() {
    const { dom, state } = this;

    dom.$component.classList.toggle(
      'has-active-filters',
      state.checkedCount > 0
    );
  }

  updateMainToggleText() {
    const { dom, state } = this;
    const textEl = dom.$mainToggleText;
    if (!textEl) return;

    textEl.textContent =
      state.checkedCount > 0 ? `Filter (${state.checkedCount})` : 'Filter';
  }

  updateMaxHeightIfRequired() {
    const { dom, state } = this;

    if (state.location !== 'mobile') return;
    if (dom.$component.classList.contains('is-collapsed')) return;

    if (state.checkedCount > 0) {
      // Add enough space so that the Clear Filters button can show with 40px margin above it
      state.formInnerHeight = state.formInnerHeight + state.mobileClearButtonOffset;
      dom.$formInner.style.maxHeight =
        (state.formInnerHeight) + 'px';
    } else {
      state.formInnerHeight = state.formInnerHeight - state.mobileClearButtonOffset;
      dom.$formInner.style.maxHeight = (state.formInnerHeight) + 'px';
    }
  }

  updateLiveRegion() {
    const { dom, state } = this;
    if (!dom.$liveRegion) return;
    dom.$liveRegion.textContent = `${state.checkedCount} filter${state.checkedCount !== 1 ? 's' : ''} selected`;
  }
}
